'use client';

import {useI18n} from '@udemy-v2/i18n';
import {ExpressiveIcon} from '@udemy-v2/icons-expressive';
import {SpotIllustration} from '@udemy-v2/illustrations';

import {FeatureCard} from './feature-card.react-component';

import styles from './feature-cards.module.scss';

/**
 * Icons are decorative and sit above the fold, so they load eagerly and at their rendered
 * size — both components otherwise emit their intrinsic dimensions (400px for
 * `SpotIllustration`, 200px for `ExpressiveIcon`).
 */
const ICON_SIZE = 32;

/**
 * English source copy for each card (title then description), used only to measure how
 * much longer a translation is. The `gettext` calls below keep their own literals so
 * `pnpm extract-locale` finds them; the English-renders-at-1 test fails if the two drift.
 */
const SOURCE_COPY = [
    'Work through problems with an expert' +
        'Bring in your own questions and learn how your instructor would approach your situation.',
    'Put your learning into practice' +
        'Grow your skills through projects, challenges, exercises, live discussions, Q&A, and feedback cycles (varies by community).',
    'Grow alongside peers with shared goals' +
        'Share experiences, compare approaches, and progress with others as tools, skills, and practices evolve.',
];

/** Beyond this the row would overflow the page for little gain; copy wraps instead. */
const MAX_WIDTH_SCALE = 1.5;

/**
 * Width factor for one card's copy: how much longer its translation is than the English
 * source, never below 1 (a card is never narrower than the design) and capped so a very
 * long translation cannot blow out the row. Exactly 1 for English.
 */
export function getWidthScale(source: string, translated: string) {
    const ratio = translated.length / source.length;
    return Math.round(Math.min(Math.max(ratio, 1), MAX_WIDTH_SCALE) * 100) / 100;
}

/**
 * The three value-prop cards below the hero — Figma node 6553:176800 (desktop, latest
 * copy) and 5260:160478 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A row of three {@link FeatureCard}s on desktop, stacked on phones, pulled up so the
 * cards overlap the bottom of the `LandingBanner` band. Copy-only — no props, no data.
 *
 * Card copy is assembled here rather than in a module-level constant so every string
 * stays a literal `gettext(…)` call for `pnpm extract-locale`.
 */
export function FeatureCards() {
    const {gettext} = useI18n();

    const cards = [
        {
            // Code Connect maps this card to a spot illustration rather than an
            // expressive icon, unlike the other two.
            icon: (
                <SpotIllustration
                    name="hands"
                    alt=""
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Work through problems with an expert'),
            description: gettext(
                'Bring in your own questions and learn how your instructor would approach your situation.',
            ),
            tone: 'orange',
        },
        {
            icon: (
                <ExpressiveIcon
                    name="team-training-2"
                    size="xsmall"
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Put your learning into practice'),
            description: gettext(
                'Grow your skills through projects, challenges, exercises, live discussions, Q&A, and feedback cycles (varies by community).',
            ),
            tone: 'green',
        },
        {
            icon: (
                <ExpressiveIcon
                    name="ai"
                    size="xsmall"
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Grow alongside peers with shared goals'),
            description: gettext(
                'Share experiences, compare approaches, and progress with others as tools, skills, and practices evolve.',
            ),
            tone: 'red',
        },
    ] as const;

    // One factor for the whole row: the cards stay the same width as each other, sized
    // for whichever card's copy has grown the most.
    const widthScale = Math.max(
        ...cards.map((card, index) =>
            getWidthScale(SOURCE_COPY[index], card.title + card.description),
        ),
    );

    return (
        <ul className={styles['cards']} data-purpose="feature-cards">
            {cards.map((card) => (
                <FeatureCard
                    key={card.title}
                    icon={card.icon}
                    title={card.title}
                    description={card.description}
                    tone={card.tone}
                    widthScale={widthScale}
                />
            ))}
        </ul>
    );
}
